import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './api-types';
import { getAccessToken, getRefreshToken, setAccessToken } from './token-storage';

// Track if we're currently refreshing to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// Auth failure callback - set by auth store to handle logout
let onAuthFailure: (() => void) | null = null;

/**
 * Set the callback to be called when authentication fails permanently
 * (i.e., refresh token is also invalid)
 */
export function setAuthFailureCallback(callback: () => void): void {
  onAuthFailure = callback;
}

/**
 * Attempt to refresh the access token.
 * With HttpOnly cookies, the refresh_token cookie is sent automatically.
 * The backend reads it from the cookie and returns updated cookies.
 * Returns true if refresh succeeded, false otherwise.
 */
async function refreshAccessToken(baseUrl: string): Promise<boolean> {
  try {
    // The refresh_token is in an HttpOnly cookie, sent automatically.
    // We still send the body for backward compatibility with iOS/native clients.
    const refreshToken = getRefreshToken();
    const response = await fetch(`${baseUrl}/v1/auth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        grant_type: 'refresh_token',
        ...(refreshToken ? { refresh_token: refreshToken } : {}),
      }),
    });

    if (!response.ok) {
      return false;
    }

    // Backend sets updated HttpOnly cookies in the response.
    // Mark the session as still active.
    setAccessToken('__refreshed__');

    return true;
  } catch {
    return false;
  }
}

/**
 * Middleware to include credentials (cookies) on every request.
 * Also injects Bearer header from legacy localStorage if present.
 */
function createBearerMiddleware(): Middleware {
  return {
    async onRequest({ request }) {
      // Legacy localStorage token support (will be removed after full migration)
      const token = getAccessToken();
      if (token && token !== '__httponly__' && token !== '__refreshed__') {
        request.headers.set('Authorization', `Bearer ${token}`);
      }
      return request;
    },
  };
}

/**
 * Middleware to handle 401 responses by attempting token refresh and retry.
 */
function createAuthMiddleware(baseUrl: string): Middleware {
  return {
    async onResponse({ response, request }) {
      // Only handle 401 responses
      if (response.status !== 401) {
        return response;
      }

      // Don't try to refresh if this is already a refresh or callback request
      const url = new URL(request.url);
      if (url.pathname.includes('/v1/auth/token') || url.pathname.includes('/v1/auth/callback')) {
        return response;
      }

      // If already refreshing, wait for that to complete
      if (isRefreshing && refreshPromise) {
        const refreshed = await refreshPromise;
        if (refreshed) {
          // Retry the original request (bearer header will be set by onRequest middleware)
          const newToken = getAccessToken();
          const retryRequest = new Request(request.clone());
          if (newToken) {
            retryRequest.headers.set('Authorization', `Bearer ${newToken}`);
          }
          const retryResponse = await fetch(retryRequest);
          return retryResponse;
        }
        // Refresh failed, return original 401
        return response;
      }

      // Start refresh process
      isRefreshing = true;
      refreshPromise = refreshAccessToken(baseUrl);

      try {
        const refreshed = await refreshPromise;

        if (refreshed) {
          // Retry the original request with new token
          const newToken = getAccessToken();
          const retryRequest = new Request(request.clone());
          if (newToken) {
            retryRequest.headers.set('Authorization', `Bearer ${newToken}`);
          }
          const retryResponse = await fetch(retryRequest);
          return retryResponse;
        }

        // Refresh failed - trigger auth failure callback
        if (onAuthFailure) {
          onAuthFailure();
        }

        return response;
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    },
  };
}

/**
 * Create a typed API client with Bearer token authentication and automatic token refresh.
 *
 * Authentication is handled via localStorage tokens.
 * The client reads the access token from localStorage and injects it as
 * `Authorization: Bearer <token>` on every request.
 *
 * On 401 responses, the client will automatically attempt to refresh the access token
 * using the stored refresh token. If refresh succeeds, the original request is retried.
 * If refresh fails, the auth failure callback is triggered (typically logs the user out).
 *
 * @param baseUrl - The base URL for the API (e.g., 'https://beta.quarkchat.io/api')
 * @returns A fully typed API client with methods for all OpenAPI endpoints
 */
export function createApiClient(baseUrl: string) {
  const client = createClient<paths>({
    baseUrl,
    credentials: 'include',
  });

  // Inject Bearer token on every request
  client.use(createBearerMiddleware());

  // Handle 401 → token refresh → retry
  client.use(createAuthMiddleware(baseUrl));

  return client;
}

/**
 * Type-safe API client type
 * Use this for typing variables that hold API clients
 */
export type ApiClient = ReturnType<typeof createApiClient>;
